import { Category } from '../types/Category';
import { Content } from '../types/Content';
import axios from 'axios';
import api from './api';
import {
  annotateCategoryFromApi,
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
  markBranchLoaded,
  markContentsLoaded,
  markTreeFullyLoaded,
  stubBranchChild,
  validateBranchLoadInvariant,
  validateContentsLoadInvariant,
  findCategoryPathInTree,
  expandedNodesForLoadedTree,
} from '../utils/categoryLoadState';

/** AI suggest-tree calls hit Java + embedding; axios has no global timeout, so the UI would spin forever. */
export const SUGGEST_TREE_REQUEST_TIMEOUT_MS = 300_000;

const trimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();

const categoryIdQuery = (categoryId: string) => {
  const qs = new URLSearchParams();
  qs.set('categoryId', trimCategoryId(categoryId));
  return qs.toString();
};

/** Load one category level for the content pane (direct children or content rows only). */
export const fetchCategoryForPane = async (categoryId: string): Promise<Category> => {
  const rootId = trimCategoryId(categoryId);
  const categoryResponse = await api.get<Category>(`/categories?${categoryIdQuery(rootId)}`);
  const category = annotateCategoryFromApi(categoryResponse.data);

  if (category.contentCategory) {
    const contentsResponse = await api.get(`/content/list?${categoryIdQuery(rootId)}`);
    const loaded = markContentsLoaded(category, contentsResponse.data as Content[]);
    validateContentsLoadInvariant(loaded, 'fetchCategoryForPane');
    return loaded;
  }

  const subcategoriesResponse = await api.get(`/categories/subcategories?${categoryIdQuery(rootId)}`);
  const children = (subcategoriesResponse.data as Category[]).map((subcategory) =>
    stubBranchChild({
      ...annotateCategoryFromApi(subcategory),
      parentId: category.id,
    }),
  );
  const loaded = markBranchLoaded(category, children);
  validateBranchLoadInvariant(loaded, 'fetchCategoryForPane');
  return loaded;
};

export const fetchCategoryWithChildren = async (categoryId: string): Promise<Category> => {
  try {
    const rootId = trimCategoryId(categoryId);
    // Explicit query string: axios params can mangle long Hebrew composite ids.
    const categoryResponse = await api.get<Category>(`/categories?${categoryIdQuery(rootId)}`);
    const category = categoryResponse.data;

    // Recursively fetch children
    const fetchChildren = async (parentCategory: Category): Promise<Category> => {
      const pid = trimCategoryId(parentCategory.id);
      // If it's a content category, fetch its contents
      if (parentCategory.contentCategory) {
        const contentsResponse = await api.get(`/content/list?${categoryIdQuery(pid)}`);
        return {
          ...parentCategory,
          contents: contentsResponse.data
        };
      }

      // Fetch subcategories
      const subcategoriesResponse = await api.get(`/categories/subcategories?${categoryIdQuery(pid)}`);
      const subcategories = subcategoriesResponse.data;
      
      if (subcategories.length === 0) {
        return parentCategory;
      }

      // Recursively fetch children for each subcategory
      const childrenPromises = subcategories.map(async (subcategory: Category) => {
        const childWithChildren = await fetchChildren(subcategory);
        return {
          ...childWithChildren,
          parentId: parentCategory.id
        };
      });

      const children = await Promise.all(childrenPromises);
      return {
        ...parentCategory,
        children,
      };
    };

    // Start the recursive fetching
    return markTreeFullyLoaded(await fetchChildren(category));
  } catch (error) {
    console.error('Error fetching category with children:', error);
    throw error;
  }
};

export const updateCategoryInTree = (cats: Category[], targetId: string, updateFn: (cat: Category) => Category): Category[] => {
    return cats.map(cat => {
    if (cat.id === targetId) {
      return updateFn(cat);
    }
    if (cat.children) {
      return {
        ...cat,
            children: updateCategoryInTree(cat.children, targetId, updateFn)
          };
        }
        return cat;
      });
    };

/** Keep already-fetched branch/content data when a branch list is refreshed from the server. */
function mergeBranchChildPreservingLoadedSubtree(
  prior: Category,
  childStub: Category,
  parentId: string,
): Category {
  if (prior.contentCategory) {
    if (categoryContentsAreLoaded(prior)) {
      return { ...prior, parentId };
    }
  } else if (branchChildrenAreLoaded(prior)) {
    return { ...prior, parentId };
  }
  return { ...childStub, parentId };
}

/** Merge a one-level branch fetch into the tree, preserving deeper loaded subtrees. */
export function mergeBranchFetchIntoTree(
  cats: Category[],
  categoryId: string,
  loaded: Category,
): Category[] {
  return updateCategoryInTree(cats, categoryId, (existing) => {
    if (loaded.contentCategory) return existing;
    const mergedChildren = (loaded.children ?? []).map((child) => {
      const prior = existing.children?.find((c) => c.id === child.id);
      return prior
        ? mergeBranchChildPreservingLoadedSubtree(prior, child, loaded.id)
        : child;
    });
    return {
      ...existing,
      ...loaded,
      children: mergedChildren,
      childrenLoadState: 'loaded',
      loadError: undefined,
    };
  });
}

export function mergeContentsFetchIntoTree(
  cats: Category[],
  categoryId: string,
  loaded: Category,
): Category[] {
  return updateCategoryInTree(cats, categoryId, () => loaded);
}

export function findCategoryInTreeRoots(roots: Category[], targetId: string): Category | null {
  for (const cat of roots) {
    if (cat.id === targetId) return cat;
    if (cat.children) {
      const found = findCategoryInTreeRoots(cat.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

/** Lazy document open: root level + branches needed for saved expansion. */
export async function loadLazyDocumentTree(
  rootCategoryId: string,
  savedExpandedIds: string[] | undefined,
): Promise<{ tree: Category[]; expandedIds: string[] }> {
  const rootCategory = await fetchCategoryForPane(rootCategoryId);
  const expandedToRestore = expandedNodesForLoadedTree(savedExpandedIds, rootCategory);
  const tree = await hydrateExpandedBranchesOnTree(
    [rootCategory],
    expandedToRestore,
    fetchCategoryForPane,
    findCategoryInTreeRoots,
  );
  return { tree, expandedIds: expandedToRestore };
}

/**
 * After {@link loadLazyDocumentTree}, load only the branch path and pane data for a saved
 * selection (not the whole document).
 */
export async function restoreSelectedCategoryInTree(
  tree: Category[],
  expandedIds: string[],
  categoryId: string | undefined,
): Promise<{ tree: Category[]; expandedIds: string[]; selectedCategory: Category | null }> {
  const root = tree[0] ?? null;
  if (!root) {
    return { tree, expandedIds, selectedCategory: null };
  }
  if (!categoryId || categoryId === root.id) {
    return { tree, expandedIds, selectedCategory: root };
  }

  let nextTree = tree;
  let nextExpanded = [...expandedIds];

  let target = findCategoryInTreeRoots(nextTree, categoryId);
  if (!target) {
    try {
      target = await fetchCategoryForPane(categoryId);
    } catch {
      return { tree, expandedIds, selectedCategory: root };
    }
  }

  const parentIds: string[] = [];
  let parentId = target.parentId;
  while (parentId) {
    parentIds.unshift(parentId);
    let parent = findCategoryInTreeRoots(nextTree, parentId);
    if (!parent) {
      try {
        parent = await fetchCategoryForPane(parentId);
      } catch {
        break;
      }
    }
    parentId = parent?.parentId;
  }

  for (const pid of parentIds) {
    nextExpanded = Array.from(new Set([...nextExpanded, pid]));
    const live = findCategoryInTreeRoots(nextTree, pid);
    if (live && !live.contentCategory && !branchChildrenAreLoaded(live)) {
      const loaded = await fetchCategoryForPane(pid);
      nextTree = mergeBranchFetchIntoTree(nextTree, pid, loaded);
    }
  }

  target = findCategoryInTreeRoots(nextTree, categoryId);
  if (!target) {
    return { tree: nextTree, expandedIds: nextExpanded, selectedCategory: root };
  }

  if (target.contentCategory && !categoryContentsAreLoaded(target)) {
    const loaded = await fetchCategoryForPane(categoryId);
    nextTree = mergeContentsFetchIntoTree(nextTree, categoryId, loaded);
    target = loaded;
  } else if (!target.contentCategory && !branchChildrenAreLoaded(target)) {
    const loaded = await fetchCategoryForPane(categoryId);
    nextTree = mergeBranchFetchIntoTree(nextTree, categoryId, loaded);
    target = loaded;
  }

  return {
    tree: nextTree,
    expandedIds: nextExpanded,
    selectedCategory: findCategoryInTreeRoots(nextTree, categoryId) ?? target,
  };
}

/** Load branch levels on the path to each expanded id (for restoring saved expansion). */
export async function hydrateExpandedBranchesOnTree(
  tree: Category[],
  expandedIds: string[],
  fetchBranch: (categoryId: string) => Promise<Category>,
  findInTree: (roots: Category[], id: string) => Category | null,
): Promise<Category[]> {
  let next = tree;
  const sorted = [...expandedIds]
    .filter(Boolean)
    .sort((a, b) => a.split(':').length - b.split(':').length);
  for (const targetId of sorted) {
    const path = findCategoryPathInTree(next, targetId);
    if (!path) continue;
    for (const node of path) {
      if (node.contentCategory) continue;
      const live = findInTree(next, node.id);
      if (!live || live.childrenLoadState === 'loaded') continue;
      const loaded = await fetchBranch(node.id);
      if (!loaded.contentCategory) {
        validateBranchLoadInvariant(loaded, 'hydrateExpandedBranchesOnTree');
      }
      next = mergeBranchFetchIntoTree(next, node.id, loaded);
    }
  }
  return next;
}

export type AddMatchingContentMatchPreview = {
  contentId: string;
  subcase: string;
  textPreview: string;
  score?: number;
};

export type CompareContentScopeType = 'CONTENT_CATEGORY' | 'BRANCH';
export type CompareContentMatchRule = 'EQUIVALENT' | 'SUBCASE_ONLY';

export type CompareContentPreview = {
  totalInSource: number;
  matchedCount: number;
  unmatchedCount: number;
  matchedContentIds: string[];
  unmatchedContentIds: string[];
};

export const previewCompareContent = async (
  sourceCategoryId: string,
  compareScopeType: CompareContentScopeType,
  compareScopeCategoryId: string,
  matchRule: CompareContentMatchRule,
) => {
  const response = await api.post('/categories/compare-content/preview', {
    sourceCategoryId,
    compareScopeType,
    compareScopeCategoryId,
    matchRule,
  });
  const data = response.data as {
    totalInSource?: number;
    matchedCount?: number;
    unmatchedCount?: number;
    matchedContentIds?: string[];
    unmatchedContentIds?: string[];
  };
  return {
    totalInSource: data.totalInSource ?? 0,
    matchedCount: data.matchedCount ?? 0,
    unmatchedCount: data.unmatchedCount ?? 0,
    matchedContentIds: data.matchedContentIds ?? [],
    unmatchedContentIds: data.unmatchedContentIds ?? [],
  } satisfies CompareContentPreview;
};

export const previewAddMatchingContent = async (targetCategoryId: string, sourceCategoryId: string) => {
  const response = await api.post('/categories/add-matching-content/preview', {
    targetCategoryId,
    sourceCategoryId,
  });
  return response.data as {
    matchCount: number;
    matches?: AddMatchingContentMatchPreview[];
    defaultSourceCategoryId?: string;
    siblingContentCategoryIds?: string[];
  };
};

export const applyAddMatchingContent = async (
  targetCategoryId: string,
  sourceCategoryId: string,
  move: boolean,
  contentIds?: string[],
) => {
  const response = await api.post('/categories/add-matching-content', {
    targetCategoryId,
    sourceCategoryId,
    move,
    ...(contentIds && contentIds.length > 0 ? { contentIds } : {}),
  });
  return response.data as {
    transferredCount: number;
    transfers: Array<{
      contentId: string;
      sourceContentId?: string;
      sourceCategoryId: string;
      targetCategoryId: string;
      moved: boolean;
    }>;
  };
};

export type AddMatchingContentTransferPayload = {
  contentId: string;
  sourceContentId?: string;
  sourceCategoryId: string;
  targetCategoryId: string;
  moved: boolean;
};

export const mapAddMatchingContentTransfers = (
  transfers: Array<{
    contentId: string;
    sourceContentId?: string;
    sourceCategoryId: string;
    targetCategoryId: string;
    moved?: boolean;
  }>,
  move: boolean,
): AddMatchingContentTransferPayload[] =>
  transfers.map((t) => ({
    contentId: t.contentId,
    sourceContentId: t.sourceContentId,
    sourceCategoryId: t.sourceCategoryId,
    targetCategoryId: t.targetCategoryId,
    moved: move,
  }));

/** Redo add-matching: re-apply copy/move for the same source row ids from a prior transfer list. */
export const replayAddMatchingContentTransfers = async (
  targetCategoryId: string,
  sourceCategoryId: string,
  move: boolean,
  transfers: AddMatchingContentTransferPayload[],
) => {
  const sourceIds = transfers
    .map((t) => t.sourceContentId ?? t.contentId)
    .filter((id): id is string => !!id);
  if (sourceIds.length === 0) {
    return [];
  }
  const data = await applyAddMatchingContent(targetCategoryId, sourceCategoryId, move, sourceIds);
  return mapAddMatchingContentTransfers(data.transfers ?? [], move);
};

/** Replay move-mode add-matching-content for redo (move assigns a new row id each time). */
export const replayAddMatchingContentMoves = async (
  transfers: AddMatchingContentTransferPayload[],
) => {
  const updated: AddMatchingContentTransferPayload[] = [];
  for (const t of transfers) {
    const response = await api.put('/content/move', null, {
      params: { contentId: t.contentId, newParentId: t.targetCategoryId },
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const moved = response.data as { id: string };
    updated.push({
      ...t,
      contentId: moved.id,
      sourceContentId: t.sourceContentId ?? t.contentId,
      moved: true,
    });
  }
  return updated;
};

export const collapseSubtreeToContentCategory = async (categoryId: string) => {
  const response = await api.post('/categories/collapse-subtree-to-content-category', { categoryId });
  return response.data as { categoryId: string; contentRowCount: number };
};

export const suggestTreeFromCategory = async (
  categoryId: string,
  maxDepth: number,
  context?: string,
  strategy: 'top_down' | 'bottom_up' = 'bottom_up',
) => {
  try {
    const response = await api.post(
      '/categories/suggest-tree',
      {
        categoryId,
        maxDepth,
        context: context || '',
        ...(strategy ? { strategy } : {}),
      },
      { timeout: SUGGEST_TREE_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    console.error('Error suggesting tree from category:', error);
    throw error;
  }
};

export const suggestParentStructureOverCategories = async (
  categoryIds: string[],
  maxDepth: number,
  context?: string,
  signal?: AbortSignal,
) => {
  const response = await api.post(
    '/categories/suggest-tree',
    {
      categoryIds,
      maxDepth,
      context: context || '',
    },
    { timeout: SUGGEST_TREE_REQUEST_TIMEOUT_MS, signal },
  );
  return response.data;
};

export type MergeCompareContentResult = {
  copiedCount: number;
  skippedDuplicateCount: number;
  createdContentIds: string[];
};

export const mergeCompareContent = async (params: {
  mergeIntoDocumentId: string;
  targetContentCategoryId: string;
  sourceContentIds: string[];
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
}): Promise<MergeCompareContentResult> => {
  const response = await api.post<MergeCompareContentResult>('/categories/merge-compare-content', {
    mergeIntoDocumentId: params.mergeIntoDocumentId,
    targetContentCategoryId: params.targetContentCategoryId,
    sourceContentIds: params.sourceContentIds,
    mergeFromDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
    ...(params.mergeFromReadAccessGrantId != null
      ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
      : {}),
  });
  return response.data;
};

export type MergeCompareCategoriesResult = {
  copiedCount: number;
  skippedDuplicateCount: number;
  createdCategoryIds: string[];
  createdBranchShellId?: string;
};

export const mergeCompareCategories = async (params: {
  mergeIntoDocumentId: string;
  targetParentCategoryId?: string;
  sourceCategoryIds: string[];
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
  createBranchShell?: { parentCategoryId: string; name: string };
}): Promise<MergeCompareCategoriesResult> => {
  const response = await api.post<MergeCompareCategoriesResult>(
    '/categories/merge-compare-categories',
    {
      mergeIntoDocumentId: params.mergeIntoDocumentId,
      targetParentCategoryId: params.targetParentCategoryId ?? '',
      sourceCategoryIds: params.sourceCategoryIds,
      mergeFromDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
      ...(params.createBranchShell ? { createBranchShell: params.createBranchShell } : {}),
    },
  );
  return response.data;
};

export type MergeCompareTypeConflictDirection = 'left' | 'right';

export type MergeCompareCategorySubtreeResult = {
  applied: boolean;
  skipped: boolean;
  transfers?: { sourceCategoryId: string; createdCategoryId: string }[];
  createdContentIds?: string[];
  createdCategoryIds?: string[];
};

export const mergeCompareCategorySubtree = async (params: {
  mergeIntoDocumentId: string;
  mergeIntoCategoryId: string;
  mergeFromCategoryId: string;
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
  treatIntoAsParent: boolean;
  resolveMissing: string;
  resolveTypeMismatch: string;
}): Promise<MergeCompareCategorySubtreeResult> => {
  const response = await api.post<MergeCompareCategorySubtreeResult>(
    '/categories/merge-compare-category-subtree',
    {
      mergeIntoDocumentId: params.mergeIntoDocumentId,
      mergeIntoCategoryId: params.mergeIntoCategoryId,
      mergeFromCategoryId: params.mergeFromCategoryId,
      mergeFromDocumentId:
        params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
      treatIntoAsParent: params.treatIntoAsParent,
      resolveMissing: params.resolveMissing,
      resolveTypeMismatch: params.resolveTypeMismatch,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
    },
  );
  return response.data;
};

export const mergeCompareTypeConflict = async (params: {
  direction: MergeCompareTypeConflictDirection;
  leftDocumentId: string;
  leftCategoryId: string;
  rightDocumentId: string;
  rightCategoryId: string;
  mergeFromReadAccessGrantId?: number;
}): Promise<{ applied: boolean; direction: string }> => {
  const response = await api.post<{ applied: boolean; direction: string }>(
    '/categories/merge-compare-type-conflict',
    {
      direction: params.direction.toUpperCase(),
      leftDocumentId: params.leftDocumentId,
      leftCategoryId: params.leftCategoryId,
      rightDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.rightDocumentId,
      rightCategoryId: params.rightCategoryId,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
    },
  );
  return response.data;
};

export type CategoryReportPreview = {
  categoryId: string;
  categoryName: string;
  scopeType: 'CONTENT_CATEGORY' | 'BRANCH';
  ancestorPath: string;
  contentRowCount: number;
  /** When true, report uses semantic ranking to pick the most relevant rows. */
  usesSemanticSelection: boolean;
  /** @deprecated use usesSemanticSelection — kept for API compatibility */
  overRowLimit?: boolean;
  maxRows: number;
};

export type CategoryReportResult = {
  report: string;
  categoryId: string;
  categoryName: string;
  scopeType: string;
  ancestorPath: string;
  contentRowCount: number;
  rowsIncluded: number;
  semanticallySelected: boolean;
};

export const previewCategoryAiReport = async (
  categoryId: string,
): Promise<CategoryReportPreview> => {
  const response = await api.post<CategoryReportPreview>('/categories/ai-report/preview', {
    categoryId,
  });
  return response.data;
};

export const generateCategoryAiReport = async (params: {
  categoryId: string;
  focus?: string;
  questions?: string;
}): Promise<CategoryReportResult> => {
  const response = await api.post<CategoryReportResult>('/categories/ai-report', params, {
    timeout: SUGGEST_TREE_REQUEST_TIMEOUT_MS,
  });
  return response.data;
};
